import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, MessageCircle, Bell, Settings, Zap, Eye, Milk } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fetchAlerts } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/competitors", icon: Building2, label: "Competitors" },
  { to: "/social", icon: MessageCircle, label: "Social" },
  { to: "/ai-visibility", icon: Eye, label: "AI Visibility" },
  { to: "/alerts", icon: Bell, label: "Alerts" },
  { to: "/dairy", icon: Milk, label: "Dairy Intel" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const location = useLocation();
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  const loadAlerts = useCallback(async () => {
    try {
      const data = await fetchAlerts({ limit: 1 });
      setUnreadAlerts(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  return (
    <div className="app-layout" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="app-sidebar" data-testid="sidebar">
        <div className="px-6 mb-8">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" style={{ color: '#FFEE2F' }} />
            <span className="text-lg font-black tracking-tight text-white" style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
              MARKET<span style={{ color: '#FFEE2F' }}>INTEL</span>
            </span>
          </div>
          <p className="text-[10px] font-semibold mt-1 tracking-wider" style={{ color: '#6B7280' }}>JB HI-FI MONITOR</p>
        </div>

        <nav className="flex-1 space-y-1">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{label}</span>
              {label === "Alerts" && unreadAlerts > 0 && (
                <Badge className="ml-auto text-white text-[9px] px-1.5 py-0 rounded-sm font-semibold" style={{ background: '#E53E3E' }}>
                  {unreadAlerts}
                </Badge>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-6 pt-4 mt-auto" style={{ borderTop: '1px solid #3D3536' }}>
          <span className="text-xs font-semibold" style={{ color: '#6B7280' }}>JB Hi-Fi Monitor</span>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main" data-testid="main-content">
        <header className="app-header">
          <div>
            <h1 className="text-base font-black tracking-wider uppercase" style={{ color: '#FFFFFF', fontFamily: 'Inter, Arial, sans-serif' }}>
              {getPageTitle(location.pathname)}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <NavLink to="/alerts" className="relative" data-testid="header-alert-bell">
              <Bell className="w-5 h-5 transition-colors duration-200" style={{ color: '#9CA3AF' }} />
              {unreadAlerts > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-[8px] font-bold flex items-center justify-center text-white" style={{ background: '#E53E3E' }}>
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </NavLink>
            <span className="text-[10px] font-semibold" style={{ color: '#9CA3AF' }}>
              {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </header>

        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function getPageTitle(path) {
  if (path === "/") return "Dashboard Overview";
  if (path === "/alerts") return "Alerts & Notifications";
  if (path === "/competitors") return "Competitor Activity";
  if (path === "/social") return "Reddit & TikTok";
  if (path.startsWith("/social/detail")) return "Social Detail";
  if (path === "/ai-visibility") return "AI Visibility";
  if (path === "/recommendations") return "All Recommendations";
  if (path === "/dairy") return "Dairy Industry Intelligence";
  if (path === "/settings") return "Settings";
  return "Market Intelligence";
}
